import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import type { PaginationState, SortingState } from '@tanstack/vue-table'
import type {
  QueryParamsByPath,
  ResponseByPath,
  SchemaAttributes,
  Schemas,
} from '@@/api/types.js'
import { useApi } from '@/composables/useApi.js'
import {
  createRelationshipBuilder,
  many,
  one,
  toPaginationQueryParams,
  type RelationshipDefs,
} from '@/utils/jsonapi.js'
import { sortingStateToQueryParam } from '@/utils/sorting.js'
const HEALTH_PLANS_LIST_URL = '/{provet_id}/api/health_plans'
const HEALTH_PLANS_DETAIL_URL = '/{provet_id}/api/health_plans/{id}'

export type HealthPlanListResponse = ResponseByPath<
  'get',
  typeof HEALTH_PLANS_LIST_URL
>
export type HealthPlanListQueryParams = QueryParamsByPath<
  'get',
  typeof HEALTH_PLANS_LIST_URL
>
export type HealthPlanDetailResponse = ResponseByPath<
  'get',
  typeof HEALTH_PLANS_DETAIL_URL
>
export type HealthPlanDetailQueryParams = QueryParamsByPath<
  'get',
  typeof HEALTH_PLANS_DETAIL_URL
>

export type HealthPlanAttributes = SchemaAttributes<'HealthPlan'>

export const HealthPlanStatuses = ['ACTIVE', 'ARCHIVED', 'DRAFT'] as const

export const HealthPlanSubscriptionStatuses = [
  'UPCOMING',
  'ACTIVE',
  'ENDED',
  'LAPSED',
  'SUSPENDED',
  'APPLIED_FOR',
  'SIGNED',
] as const

const healthPlansKeys = {
  all: ['healthPlans'] as const,
  lists: () => [...healthPlansKeys.all, 'list'] as const,
  list: (query: MaybeRef<HealthPlanListQueryParams>) =>
    [...healthPlansKeys.lists(), query] as const,
  detail: (id: MaybeRef<string>) =>
    [...healthPlansKeys.all, 'detail', id] as const,
}

export function useHealthPlanDetailQuery({
  healthPlanId,
  includes = [],
  enabled = true,
}: {
  healthPlanId: MaybeRef<string>
  includes?: MaybeRef<string[]>
  enabled?: MaybeRef<boolean>
}) {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    queryKey: healthPlansKeys.detail(healthPlanId),
    queryFn: async () =>
      await api((client) =>
        client.GET(HEALTH_PLANS_DETAIL_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              id: unref(healthPlanId),
            },
            query: {
              include: unref(includes),
            },
          },
        }),
      ),
    enabled,
  })

  return reactive(query)
}

export function useHealthPlanListQuery({
  pagination,
  sorting,
  filters = {},
  includes = [],
}: {
  pagination: Ref<PaginationState>
  sorting: Ref<SortingState>
  filters?: MaybeRef<Partial<HealthPlanListQueryParams>>
  includes?: MaybeRef<string[]>
}) {
  const api = useApi()
  const route = useRoute()

  const queries = computed<HealthPlanListQueryParams>(() => ({
    ...toPaginationQueryParams(pagination.value),
    sort: sortingStateToQueryParam(sorting.value),
    include: unref(includes),
    ...unref(filters),
  }))

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: healthPlansKeys.list(queries),
    queryFn: async () =>
      await api((client) =>
        client.GET(HEALTH_PLANS_LIST_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queries.value,
          },
        }),
      ),
  })

  return reactive(query)
}

const healthPlanRelationships = {
  department: one('Department'),
  additional_departments: many('Department'),
  species: one('ListItem'),
  // invoicing_item: one('Item'),
  // compensation_item: one('Item'),
  // cancellation_fee_item: one('Item'),
  default_renewal_plan: one('HealthPlan'),
  client: one('Client'),
  patient: one('Patient'),
} satisfies RelationshipDefs<Schemas['HealthPlan']>

export const buildHealthPlanRelationships = createRelationshipBuilder(
  healthPlanRelationships,
)
